/**
 * @file This file contains the AmbientAnimalManager class for creating ambient, non-interactive flying animals.
 * @module AmbientAnimalManager
 */

import { ambientAnimalsConfig } from '../config/ambientAnimalsConfig.js';

export class AmbientAnimalManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {HTMLElement} dependencies.overlayElement - The DOM element to render effects into.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     */
    constructor({ overlayElement, eventEmitter }) {
        this.overlay = overlayElement;
        this.eventEmitter = eventEmitter;
        this.config = ambientAnimalsConfig;
        this.particles = [];
        this.activeFlock = false; // Flag to indicate if the flock event is active
        this.turnsRemaining = 0;
        this.turnCounter = 0;
        this.gameConfig = null;

        this.eventEmitter.on('achievement:turnEnded', () => this.onTurnEnd());
    }

    setGameConfig(gameConfig) {
        this.gameConfig = gameConfig;
    }

    onTurnEnd() {
        if (this.activeFlock) {
            this.turnsRemaining--;
            if (this.turnsRemaining <= 0) {
                this.stopFlock();
            }
        } else {
            this.turnCounter++;
            if (this.turnCounter >= this.config.frequency) {
                if (Math.random() < 1 / this.config.frequency) {
                    this.startFlock();
                }
            }
        }
    }

    startFlock() {
        if (this.activeFlock) return;

        this.activeFlock = true;
        this.turnsRemaining = this.config.duration;
        this.turnCounter = 0;

        // Spawn initial particles
        for (let i = 0; i < this.config.particle.count; i++) {
            this.createParticle(true);
        }
    }

    stopFlock() {
        this.activeFlock = false;
        this.turnsRemaining = 0;
    }

    /**
     * A cheat function to immediately start the flock event.
     */
    forceStartFlock() {
        this.stopFlock();
        this.startFlock();
    }

    createParticle(isInitial = false) {
        if (!this.activeFlock || this.config.animals.length === 0) return;

        const particle = document.createElement('div');
        const displayContent = this.config.animals[Math.floor(Math.random() * this.config.animals.length)];
        particle.textContent = displayContent;

        particle.style.position = 'absolute';
        particle.style.fontSize = '1.5em';
        particle.style.userSelect = 'none';
        particle.classList.add('ambient-animal');

        const min = this.config.particle.depthMin || 0.5;
        const max = this.config.particle.depthMax || 1.5;
        const depth = Math.random() * (max - min) + min;

        particle.style.zIndex = Math.round(depth * 10);

        const startsOnLeft = Math.random() < 0.5;
        const x = startsOnLeft ? -100 : (this.overlay.offsetWidth || window.innerWidth) + 100;
        const y = Math.random() * (this.overlay.offsetHeight || window.innerHeight) * 0.8;

        this.overlay.appendChild(particle);
        this.particles.push({
            element: particle,
            x,
            y,
            speed: (this.config.particle.baseSpeed || 2) + Math.random() * 2,
            wind: (this.config.particle.wind || 0) * (Math.random() - 0.5) * 2,
            direction: startsOnLeft ? 1 : -1, // 1 for right, -1 for left
            angle: Math.random() * Math.PI * 2,
            depth: depth
        });

        particle.style.transform = `translate3d(${x}px, ${y}px, 49px) scale(${depth})`;
    }

    update() {
        if (!this.activeFlock && this.particles.length === 0) return;

        const overlayWidth = this.overlay.offsetWidth || window.innerWidth;

        // Randomly spawn new particles during an active flock event
        if (this.activeFlock && Math.random() < this.config.particle.spawnChanceDuringFlock) {
            this.createParticle();
        }

        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];

            p.x += p.speed * p.depth * p.direction;
            p.y += (p.wind * p.depth) + Math.sin(p.angle) * 0.2;
            p.angle += 0.03;

            const isOffScreen = (p.direction === 1 && p.x > overlayWidth + 120) || (p.direction === -1 && p.x < -120);

            if (isOffScreen) {
                p.element.remove();
                this.particles.splice(i, 1);
                continue;
            }

            // The direction is 1 for right, -1 for left.
            // The emojis face left by default, so we need to flip them with scaleX(-1) when they move right.
            p.element.style.transform = `translate3d(${p.x}px, ${p.y}px, 49px) scale(${p.depth}) scaleX(${-p.direction})`;
        }
    }

}   